import useSWR from 'swr';
import { backendClient } from '@/lib/api-client';

export interface StageProgress {
  done: number;
  total: number;
}

export interface ChronologyRefreshStatus {
  chronology_id: string;
  name: string;
  chronology_type: string;
  synced_to_latest_trunk: boolean;
  /** Not synced, and its builds wait until the matter's documents are upgraded. */
  waiting_for_documents?: boolean;
  remaining_cell_dates?: number;
  remaining_cell_revision?: string;
  projected_documents: number;
  available_documents: number;
  /** Periods a running first build has committed; null when not drafting. */
  drafted_periods?: number | null;
}

export interface PipelineProgress {
  total_documents: number;
  /** Latest ``entities_consolidated`` event time. Moves on reconsolidation of the same docs. */
  entities_updated_at?: string | null;
  /** Last successful entity-cleanup watermark ``ran_at``. Moves when tidy-up finishes. */
  entities_cleanup_at?: string | null;
  scope?: {
    total: number;
    included: number;
    queued: number;
    processing: number;
    current: number;
    removing: number;
  };
  processing: boolean;
  stages: {
    interpreted: StageProgress;
    extracted: StageProgress;
    linked: StageProgress;
    entities_consolidated: StageProgress;
    chronology_consolidated: StageProgress;
  };
  projections: ChronologyRefreshStatus[];
}

export function usePipelineProgress(matterGuid: string | undefined) {
  const { data, error, isLoading, mutate } = useSWR(
    matterGuid ? `/matters/${matterGuid}/pipeline-progress` : null,
    async (url: string) => {
      const response = await backendClient.get(url);
      return response.data as PipelineProgress;
    },
    {
      dedupingInterval: 5000,
      refreshInterval: (latestData: PipelineProgress | undefined) => {
        const unsynced = latestData?.projections?.filter(projection => !projection.synced_to_latest_trunk) ?? [];
        if (latestData?.processing || unsynced.some(projection => !projection.waiting_for_documents)) return 8000;
        return unsynced.length > 0 ? 60000 : 0;
      },
    }
  );

  return {
    progress: data ?? null,
    isLoading,
    isError: !!error,
    mutate,
  };
}
